import React, { useState, useEffect, useMemo, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import {
  Calendar, Download, Plus, Search, Loader2, Lock, Unlock,
  X, Save, Filter, RefreshCw, Users,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SHIFT_COLORS = {
  GEN: 'bg-blue-50 text-blue-700 border-blue-300',
  NIGHT: 'bg-purple-50 text-purple-700 border-purple-300',
  WO: 'bg-slate-100 text-slate-500 border-slate-300',
  HOLIDAY: 'bg-green-50 text-green-700 border-green-300',
  EVENING: 'bg-amber-50 text-amber-700 border-amber-300',
  SPLIT: 'bg-pink-50 text-pink-700 border-pink-300',
};

const ShiftRoster = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [employees, setEmployees] = useState([]);
  const [roster, setRoster] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  });
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [showAddShift, setShowAddShift] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkForm, setBulkForm] = useState({
    shift_id: '', start_date: '', end_date: '',
    weekly_offs: ['Saturday', 'Sunday'],
    assign_by: 'selected', employee_ids: [], group_ids: [], department_ids: [],
  });
  const [shiftForm, setShiftForm] = useState({ shift_name: '', shift_code: '', start_time: '09:00', end_time: '18:00' });

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
      setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [year, month] = selectedMonth.split('-').map(Number);
      const daysInMonth = new Date(year, month, 0).getDate();
      const startDate = `${selectedMonth}-01`;
      const endDate = `${selectedMonth}-${String(daysInMonth).padStart(2,'0')}`;
      const [empRes, rosterRes, shiftRes] = await Promise.all([
        axios.get(`${API_URL}/employees?company_id=${selectedCompany}`, { withCredentials: true }),
        axios.get(`${API_URL}/attendance/roster?start_date=${startDate}&end_date=${endDate}`, { withCredentials: true }),
        axios.get(`${API_URL}/attendance/shifts?company_id=${selectedCompany}`, { withCredentials: true }),
      ]);
      setEmployees(empRes.data || []);
      setRoster(rosterRes.data || []);
      setShifts(shiftRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedCompany, selectedMonth]);

  useEffect(() => { if (selectedCompany) { fetchAll(); } }, [selectedCompany, selectedMonth, fetchAll]);



  const dates = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => {
      const d = new Date(year, month - 1, i + 1);
      return { date: d.toISOString().split('T')[0], day: d.toLocaleDateString('en', { weekday: 'short' }), num: i + 1 };
    });
  }, [selectedMonth]);

  const rosterMap = useMemo(() => {
    const map = {};
    roster.forEach(r => {
      const key = `${r.employee_id}_${r.roster_date}`;
      map[key] = r;
    });
    return map;
  }, [roster]);

  const departments = useMemo(() => {
    const depts = new Set();
    employees.forEach(e => { const d = e.employment_details?.department; if (d) depts.add(d); });
    return [...depts].sort();
  }, [employees]);

  const filteredEmployees = employees.filter(e => {
    const name = `${e.personal_details?.first_name || ''} ${e.personal_details?.last_name || ''}`.toLowerCase();
    const code = e.employment_details?.employee_code?.toLowerCase() || '';
    const dept = e.employment_details?.department || '';
    const matchSearch = !search || name.includes(search.toLowerCase()) || code.includes(search.toLowerCase());
    const matchDept = deptFilter === 'all' || dept === deptFilter;
    return matchSearch && matchDept;
  });

  const getShiftLabel = (empId, dateStr) => {
    const entry = rosterMap[`${empId}_${dateStr}`];
    if (!entry) return '';
    if (entry.is_weekly_off) return 'WO';
    if (entry.is_holiday) return 'HOL';
    const shift = shifts.find(s => s.id === entry.shift_id);
    return shift?.shift_code || entry.shift_id?.substring(0, 3)?.toUpperCase() || 'GEN';
  };

  const getShiftColor = (label) => SHIFT_COLORS[label] || 'bg-slate-50 text-slate-600 border-slate-200';

  const handleBulkAssign = async () => {
    if (!bulkForm.shift_id || !bulkForm.start_date || !bulkForm.end_date) {
      toast.error('Shift, start date, and end date are required'); return;
    }
    setSaving(true);
    try {
      const payload = { ...bulkForm, company_id: selectedCompany };
      if (bulkForm.assign_by === 'selected' && bulkForm.employee_ids.length === 0) {
        payload.employee_ids = filteredEmployees.map(e => e.id);
      }
      const { data } = await axios.post(`${API_URL}/attendance/roster/bulk-assign`, payload, { withCredentials: true });
      toast.success(`${data.message} (${data.count} entries)`);
      setShowBulkAssign(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Assignment failed'); }
    finally { setSaving(false); }
  };

  const handleAddShift = async () => {
    if (!shiftForm.shift_name || !shiftForm.shift_code) { toast.error('Shift name and code required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/attendance/shifts`, { ...shiftForm, company_id: selectedCompany }, { withCredentials: true });
      toast.success('Shift created');
      setShowAddShift(false);
      setShiftForm({ shift_name: '', shift_code: '', start_time: '09:00', end_time: '18:00' });
      fetchAll();
    } catch (e) { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const handleLockRoster = async () => {
    try {
      await axios.post(`${API_URL}/attendance/roster/lock?company_id=${selectedCompany}&period=${selectedMonth}`, {}, { withCredentials: true });
      toast.success('Roster locked');
      fetchAll();
    } catch (e) { toast.error('Lock failed'); }
  };

  const handleUnlockRoster = async () => {
    try {
      await axios.post(`${API_URL}/attendance/roster/unlock?company_id=${selectedCompany}&period=${selectedMonth}`, {}, { withCredentials: true });
      toast.success('Roster unlocked');
      fetchAll();
    } catch (e) { toast.error('Unlock failed'); }
  };

  const lockedCount = roster.filter(r => r.status === 'locked').length;

  return (
    <MainLayout>
      <div className="space-y-5" data-testid="shift-roster-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <Calendar className="inline-block w-8 h-8 mr-2 mb-1" /> Shift Roster
            </h1>
            <p className="mt-1 text-sm text-slate-600">Plan and manage employee shift schedules</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" data-testid="roster-add-shift-btn" onClick={() => setShowAddShift(true)}>
              <Plus className="w-4 h-4 mr-1" /> Add Shift
            </Button>
            <Button variant="outline" size="sm" data-testid="roster-export-btn" onClick={() => {
              if (filteredEmployees.length === 0) { toast.error('No data'); return; }
              const rows = [];
              filteredEmployees.forEach(e => {
                const row = { Employee: `${e.personal_details?.first_name} ${e.personal_details?.last_name}`, Code: e.employment_details?.employee_code };
                dates.forEach(d => { row[d.date] = getShiftLabel(e.id, d.date); });
                rows.push(row);
              });
              downloadCSV(rows, `shift_roster_${selectedMonth}.csv`);
              toast.success('Exported');
            }}>
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
            {lockedCount > 0 && (
              <Button variant="outline" size="sm" data-testid="roster-unlock-btn" onClick={handleUnlockRoster}>
                <Unlock className="w-4 h-4 mr-1" /> Unlock
              </Button>
            )}
            <Button variant="outline" size="sm" data-testid="roster-lock-btn" onClick={handleLockRoster}>
              <Lock className="w-4 h-4 mr-1" /> Lock
            </Button>
            <Button size="sm" data-testid="roster-bulk-assign-btn" onClick={() => {
              setBulkForm({ ...bulkForm, start_date: `${selectedMonth}-01`, end_date: dates.length > 0 ? dates[dates.length-1].date : '', shift_id: shifts[0]?.id || '' });
              setShowBulkAssign(true);
            }} className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
              <Users className="w-4 h-4 mr-1" /> Bulk Assign
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)} data-testid="roster-company-select"
            className="h-9 px-3 bg-white border border-slate-300 rounded-md text-sm">
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} data-testid="roster-month-select"
            className="h-9 px-3 bg-white border border-slate-300 rounded-md text-sm" />
          <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} data-testid="roster-dept-filter"
            className="h-9 px-3 bg-white border border-slate-300 rounded-md text-sm">
            <option value="all">All Departments</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input data-testid="roster-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search employee..." className="w-full h-9 pl-9 pr-3 bg-white border border-slate-300 rounded-md text-sm" />
          </div>
          <div className="flex gap-2 ml-auto">
            {Object.entries({ GEN: 'General', NIGHT: 'Night', WO: 'Off', EVENING: 'Evening' }).map(([k, v]) => (
              <div key={k} className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded border ${getShiftColor(k)}`}></div>
                <span className="text-[10px] text-slate-500">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Roster Grid */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="roster-grid">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="sticky left-0 z-10 bg-slate-50 text-left py-2 px-3 font-semibold text-slate-500 uppercase border-r border-slate-200 min-w-[160px]">Employee</th>
                {dates.map(d => (
                  <th key={d.date} className={`text-center py-2 px-1 font-semibold uppercase min-w-[36px] ${d.day === 'Sun' || d.day === 'Sat' ? 'text-red-400 bg-red-50/30' : 'text-slate-500'}`}>
                    <div>{d.num}</div>
                    <div className="text-[9px] font-normal">{d.day}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={dates.length + 1} className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></td></tr>
              ) : filteredEmployees.length === 0 ? (
                <tr><td colSpan={dates.length + 1} className="text-center py-12 text-slate-500">No employees found</td></tr>
              ) : filteredEmployees.map(emp => (
                <tr key={emp.id} data-testid={`roster-row-${emp.employment_details?.employee_code}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="sticky left-0 z-10 bg-white py-1.5 px-3 border-r border-slate-200">
                    <p className="text-xs font-medium text-slate-900 truncate">{emp.personal_details?.first_name} {emp.personal_details?.last_name}</p>
                    <p className="text-[10px] text-slate-400 font-mono">{emp.employment_details?.employee_code}</p>
                  </td>
                  {dates.map(d => {
                    const label = getShiftLabel(emp.id, d.date);
                    return (
                      <td key={d.date} className={`py-1 px-0.5 text-center ${d.day === 'Sun' || d.day === 'Sat' ? 'bg-red-50/20' : ''}`}>
                        {label ? (
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold border ${getShiftColor(label)}`}>{label}</span>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-slate-400">{filteredEmployees.length} employees, {roster.length} roster entries ({lockedCount} locked)</p>

        {/* Bulk Assign Modal */}
        {showBulkAssign && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bulk-assign-modal">
            <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh]">
              <div className="flex items-center justify-between p-5 border-b bg-slate-50 rounded-t-xl flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#0052FF]" />
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Bulk Assign Roster</h2>
                    <p className="text-xs text-slate-500">Assign shifts to multiple employees</p>
                  </div>
                </div>
                <button onClick={() => setShowBulkAssign(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5 space-y-4 overflow-y-auto flex-1">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Assign By</label>
                  <select data-testid="bulk-assign-by" value={bulkForm.assign_by} onChange={e => setBulkForm({...bulkForm, assign_by: e.target.value})}
                    className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm">
                    <option value="selected">All Filtered Employees</option>
                    <option value="department">By Department</option>
                    <option value="group">By CTC Group</option>
                    <option value="location">By Location</option>
                    <option value="category">By Category</option>
                  </select>
                </div>
                {bulkForm.assign_by === 'department' && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Department(s)</label>
                    <select data-testid="bulk-assign-dept" multiple size={3} value={bulkForm.department_ids}
                      onChange={e => setBulkForm({...bulkForm, department_ids: [...e.target.selectedOptions].map(o => o.value)})}
                      className="w-full border-2 border-slate-200 rounded-lg px-3 py-1 text-sm">
                      {departments.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Shift *</label>
                  <select data-testid="bulk-assign-shift" value={bulkForm.shift_id} onChange={e => setBulkForm({...bulkForm, shift_id: e.target.value})}
                    className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm">
                    <option value="">Select Shift</option>
                    {shifts.map(s => <option key={s.id} value={s.id}>{`${s.shift_name} (${s.shift_code}) — ${s.start_time} to ${s.end_time}`}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Start Date *</label>
                    <input data-testid="bulk-assign-start" type="date" value={bulkForm.start_date}
                      onChange={e => setBulkForm({...bulkForm, start_date: e.target.value})}
                      className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">End Date *</label>
                    <input data-testid="bulk-assign-end" type="date" value={bulkForm.end_date}
                      onChange={e => setBulkForm({...bulkForm, end_date: e.target.value})}
                      className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Weekly Offs</label>
                  <div className="flex flex-wrap gap-2">
                    {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(day => (
                      <label key={day} className="flex items-center gap-1 text-xs cursor-pointer">
                        <input type="checkbox" checked={bulkForm.weekly_offs.includes(day)}
                          onChange={e => {
                            const offs = e.target.checked ? [...bulkForm.weekly_offs, day] : bulkForm.weekly_offs.filter(d => d !== day);
                            setBulkForm({...bulkForm, weekly_offs: offs});
                          }} className="rounded border-slate-300 text-blue-600" />
                        {day.substring(0, 3)}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 p-5 border-t bg-slate-50 rounded-b-xl flex-shrink-0">
                <Button variant="outline" onClick={() => setShowBulkAssign(false)}>Cancel</Button>
                <Button data-testid="bulk-assign-submit" onClick={handleBulkAssign} disabled={saving} className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Assign Roster
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Add Shift Modal */}
        {showAddShift && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="add-shift-modal">
            <div className="bg-white rounded-lg w-full max-w-sm flex flex-col max-h-[90vh]">
              <div className="flex items-center justify-between p-5 border-b flex-shrink-0">
                <h2 className="text-lg font-semibold">Add Shift Definition</h2>
                <button onClick={() => setShowAddShift(false)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>
              <div className="p-5 space-y-3 overflow-y-auto flex-1">
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Shift Name *</label>
                  <input data-testid="shift-name-input" value={shiftForm.shift_name} onChange={e => setShiftForm({...shiftForm, shift_name: e.target.value})}
                    placeholder="General Shift" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" /></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Shift Code *</label>
                  <input data-testid="shift-code-input" value={shiftForm.shift_code} onChange={e => setShiftForm({...shiftForm, shift_code: e.target.value.toUpperCase()})}
                    placeholder="GEN" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">Start Time</label>
                    <input type="time" value={shiftForm.start_time} onChange={e => setShiftForm({...shiftForm, start_time: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" /></div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">End Time</label>
                    <input type="time" value={shiftForm.end_time} onChange={e => setShiftForm({...shiftForm, end_time: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" /></div>
                </div>
              </div>
              <div className="flex justify-end gap-2 p-5 border-t flex-shrink-0">
                <Button variant="outline" onClick={() => setShowAddShift(false)}>Cancel</Button>
                <Button data-testid="save-shift-btn" onClick={handleAddShift} disabled={saving} className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ShiftRoster;
